/**
 * The Health tab's Eva card (SF-5117): whether the company has Eva switched on and used it in
 * the month, then its messages and cost this month against last month with the % change. Last
 * month covers the same days while the month is in progress. Eva's figures come from daily rows
 * dated in UTC, so they can be off by up to a day at the month edges.
 */
import { Descriptions, Tag } from 'antd';
import { TrendText } from '../../../../components/usage/TrendText';
import type { CompanyEvaUsage } from '../../../../components/usage/usageClient';
import { formatCurrency, formatNumber } from '../../../../utils/format';

export interface EvaCardProps {
  eva: CompanyEvaUsage;
  currentLabel: string;
  previousLabel: string;
  /** E.g. "vs last month" or "vs same days last month". */
  caption: string;
}

const yesNo = (v: boolean) => (v ? 'Yes' : 'No');

export const EvaCard: React.FC<EvaCardProps> = ({ eva, currentLabel, previousLabel, caption }) => {
  const rows = [
    {
      key: 'messages',
      label: 'Messages',
      current: eva.this_month?.messages,
      previous: eva.last_month?.messages,
      format: formatNumber,
    },
    {
      key: 'cost',
      label: 'Cost',
      current: eva.this_month?.cost_usd,
      previous: eva.last_month?.cost_usd,
      format: (v: number | undefined) => formatCurrency(v),
    },
  ];
  return (
    <div data-testid="health-eva">
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="Switched on">{yesNo(eva.switched_on)}</Descriptions.Item>
        <Descriptions.Item label="Used this month">
          {eva.used ? (
            'Yes'
          ) : eva.switched_on ? (
            <Tag color="warning">Switched on, not used</Tag>
          ) : (
            'No'
          )}
        </Descriptions.Item>
      </Descriptions>
      <table
        aria-label="Eva use"
        style={{ width: '100%', marginTop: 12, borderCollapse: 'collapse' }}
      >
        <thead>
          <tr>
            <td />
            <th scope="col" style={{ textAlign: 'right', padding: '4px 8px' }}>
              {currentLabel}
            </th>
            <th scope="col" style={{ textAlign: 'right', padding: '4px 8px' }}>
              {previousLabel}
            </th>
            <th scope="col" style={{ textAlign: 'right', padding: '4px 8px' }}>
              {`Change ${caption}`}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <th scope="row" style={{ textAlign: 'left', padding: '4px 8px' }}>
                {r.label}
              </th>
              <td style={{ textAlign: 'right', padding: '4px 8px' }}>{r.format(r.current)}</td>
              <td style={{ textAlign: 'right', padding: '4px 8px' }}>{r.format(r.previous)}</td>
              <td style={{ textAlign: 'right', padding: '4px 8px' }}>
                {/* Cost rises with use, so a rise is green here too: more use is the aim. */}
                <TrendText current={r.current} previous={r.previous} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
